import React, { useState, useEffect } from 'react';
import { Alert, Box, Paper, Typography, TextField, CircularProgress } from '@mui/material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import EmailAuthGate from './EmailAuthGate';
import CountdownTimer from './CountdownTimer';

const ViewSecret = ({ secretId, decryptKey }) => {
    const [secret, setSecret] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [maxReadCount, setMaxReadCount] = useState(0);
    const [currentReadCount, setCurrentReadCount] = useState(0);
    const [expireDate, setExpireDate] = useState(null);
    const [lastAccessedAt, setLastAccessedAt] = useState(null);
    const [validAt, setValidAt] = useState(null);
    const [requireEmailAuth, setRequireEmailAuth] = useState(false);
    const [authChecked, setAuthChecked] = useState(false);
    const [secretLoaded, setSecretLoaded] = useState(false);
    const [oneTime, setOneTime] = useState(false);
    const [burnAfterRead, setBurnAfterRead] = useState(0);
    const [user, setUser] = useState(null);

    const auth = getAuth();
    //const user = auth.currentUser;

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
          if (currentUser) {
            // User is signed in, update the user state
            setUser(currentUser);
          } else {
            // User is signed out, reset the user state
            setUser(null);
          }
        });
      
        // Cleanup listener on component unmount
        return () => unsubscribe();
      }, []);
    

    useEffect(() => {
        const fetchSecret = async () => {
            const functions = getFunctions();
            const getSecretMeta = httpsCallable(functions, 'getSecretMeta');
    
            try {
                const result = await getSecretMeta({ secretId });
    
                if (result.data.success) {
                    const { oneTime, requiredEmailAuth, secret, currentReadCount, maxReadCount, validAt, validUntil, lastAccessedAt } = result.data;
                    setRequireEmailAuth(requiredEmailAuth);
                    setCurrentReadCount(currentReadCount);
                    setMaxReadCount(maxReadCount);
                    setExpireDate(validUntil);
                    setLastAccessedAt(lastAccessedAt);
                    setOneTime(oneTime);
                    setBurnAfterRead(result.data.burnAfterRead);
                    setValidAt(validAt);
    
                    if (requiredEmailAuth && !user) {
                        setSecretLoaded(true);
                        return;  // Prevent decryption and show email auth gate
                    }
    
                    const functions = getFunctions();
                    const getSecret = httpsCallable(functions, 'getSecret');
                    const secretResult = await getSecret({ secretId });
                    const decryptedSecret = await decryptSecret(secretResult.data.secret, decryptKey, secretResult.data.iv);
                    setSecret(decryptedSecret);
                    setIsLoading(false);
                    setSecretLoaded(true);

                    // For one-time secrets, delete only after the secret was
                    // successfully retrieved and decrypted for display.
                    if (oneTime && decryptedSecret !== null) {
                        await deleteSecretAfterOneTimeUse();
                    }
                } else {
                    setError(result.data.message);
                    setIsLoading(false);
                    setSecretLoaded(true);
                }
            } catch (error) {
                console.error('Error fetching secret:', error);
                setError('Error fetching secret');
                setIsLoading(false);
                setSecretLoaded(true);
            }
        };

        const deleteSecretAfterOneTimeUse = async () => {
            const functions = getFunctions();
            const deleteSecret = httpsCallable(functions, 'deleteSecret');
            await deleteSecret({ secretId });
        }
    
        if (secretId && decryptKey && !secretLoaded) {
            fetchSecret();
        }
    }, [secretId, decryptKey, user, secretLoaded]);
    

    // Decrypting the secret using the Web Crypto API (AES-256-GCM)
    const decryptSecret = async (encryptedText, keyBase64, iv) => {
        try {
            const decoder = new TextDecoder();

            // Base64 -> Uint8Array
            const fromBase64 = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

            const encryptedData = fromBase64(encryptedText);
            const keyBytes = fromBase64(keyBase64);
            const ivBytes = fromBase64(iv);

            const key = await crypto.subtle.importKey(
                'raw',
                keyBytes,
                { name: 'AES-GCM' },
                false,
                ['decrypt']
            );

            // Decryption throws if the auth tag does not verify (tampering or
            // a wrong key), so a returned value is guaranteed authentic.
            const decryptedBuffer = await crypto.subtle.decrypt(
                {
                name: 'AES-GCM',
                iv: ivBytes,
                },
                key,
                encryptedData
            );

            return decoder.decode(decryptedBuffer);
        } catch (error) {
            setError('Secret is not available');
            setIsLoading(false);
            setSecretLoaded(true);
            return null;
        }
    };

    if (!secretLoaded) {
        console.log("Secret not loaded yet");
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (requireEmailAuth && !user) {
        console.log("Email auth required");
        return <EmailAuthGate secretId={secretId} decryptKey={decryptKey}/>;
    }

    return (
        <Box sx={{ mt: 3 }}>
            {isLoading ? (
                <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100vh', // or any height you need
                }}
              >
                <CircularProgress />
              </Box>
            ) : error ? (
                <Paper elevation={3} sx={{ p: 3, width: '100%', maxWidth: 1200, margin: 'auto' }}>
                    <Typography color="error" variant="h6" gutterBottom>
                        Error: {error}
                    </Typography>
                </Paper>
            ) : (
                <Paper elevation={3} sx={{ p: 3, width: '100%', maxWidth: 1200, margin: 'auto' }}>
                    {burnAfterRead > 0 && (
                        <CountdownTimer burnAfterReading={burnAfterRead} secId={secretId} />
                    )}
                    <Typography variant="h6" gutterBottom>
                        View Secret
                    </Typography>
                    <TextField
                        label="Your Secret"
                        multiline
                        rows={5}
                        fullWidth
                        value={secret}
                        InputProps={{
                            readOnly: true,
                            style: { fontFamily: 'Consolas, monospace' }, // Set the font to Consolas
                        }}
                    />
                    {/* Metadata Section */}

                    
                    <Box sx={{ mt: 2 }}>

                        {oneTime ? (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                            This secret is configured for one time use. The secret will not be accessible if you leave this page.
                            </Alert>
                        ) : (<></>)}

                        {currentReadCount >= maxReadCount - 4 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            This secret is about to expire. After the maxReadCount is reached you can't access it anymore. Please save it before expiration.
                        </Alert>
                        )}


                        <Typography variant="body1">
                        <strong>Max Read Count:</strong> {maxReadCount}
                        </Typography>
                        <Typography variant="body1">
                        <strong>Current Read Count:</strong> {currentReadCount}
                        </Typography>
                        <Typography variant="body1">
                        <strong>Expiration Date:</strong> {new Date(expireDate).toLocaleString()}
                        </Typography>
                        <Typography variant="body1">
                        <strong>Last Access Date:</strong> {new Date(lastAccessedAt).toLocaleString()}
                        </Typography>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default ViewSecret;
